/**
 * Reports list and detail, BUILD.md §9.
 *
 * Detail shows the stored snapshot's scores, offers a PDF download, a re-render
 * from snapshot, and a "notify patient" action (§11).
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type { ReportModel } from '@mcos/types';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatBytes, formatDate, formatDateTime, fullName } from '../lib/format';
import {
  DomainScorePills,
  Button,
  Card,
  EmptyState,
  ErrorState,
  AlertBadge,
  LoadingRows,
  PageHeader,
  Pill,
  ScoreGauge,
  ScorePill,
  Table,
  Td,
  Th,
  TrendArrow,
} from '../components/ui';
import { IconDownload } from '../components/icons';

interface ReportRow {
  id: string;
  generatedAt: string;
  comparisonType: string;
  pdfBytes: number | null;
  patient: { id: string; firstName: string; lastName: string };
  labReport: { id: string; collectionDate: string; status: string };
  scoreRun: {
    overallScore: number | null;
    metabolicScore: number | null;
    inflammationScore: number | null;
    liverScore: number | null;
    confidence: string;
    validationStatus: string;
  };
}

export function ReportsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports'],
    queryFn: () =>
      api.get<{ data: ReportRow[]; total: number }>(`/reports${qs({ pageSize: 100 })}`),
  });

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Every generated wellness report, newest first."
      />

      <Card padded={false}>
        {error ? (
          <div className="p-5"><ErrorState error={error} retry={() => void refetch()} /></div>
        ) : isLoading ? (
          <div className="p-5"><LoadingRows rows={6} /></div>
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            title="No reports yet"
            body="Verify a lab report's values and the wellness report can be generated."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Patient</Th>
                <Th>Collection date</Th>
                <Th>Generated</Th>
                <Th align="center">Type</Th>
                <Th align="center">Confidence</Th>
                <Th align="right">Scores</Th>
                <Th align="right">Size</Th>
                <Th align="right"> </Th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((report) => (
                <tr key={report.id} className="hover:bg-ink/5/70">
                  <Td className="max-sm:whitespace-nowrap">
                    <Link
                      to={`/patients/${report.patient.id}`}
                      className="font-medium text-ink hover:text-[var(--brand-accent)]"
                    >
                      {fullName(report.patient)}
                    </Link>
                  </Td>
                  <Td className="text-ink-muted max-sm:whitespace-nowrap">{formatDate(report.labReport.collectionDate)}</Td>
                  <Td className="text-ink-muted text-xs max-sm:whitespace-nowrap">{formatDateTime(report.generatedAt)}</Td>
                  <Td align="center" className="text-xs text-ink-muted capitalize">
                    {report.comparisonType.toLowerCase()}
                  </Td>
                  <Td align="center" className="text-xs text-ink-muted">
                    {report.scoreRun.confidence}
                  </Td>
                  <Td align="right">
                    <DomainScorePills
                      metabolic={report.scoreRun.metabolicScore}
                      inflammation={report.scoreRun.inflammationScore}
                      liver={report.scoreRun.liverScore}
                    />
                  </Td>
                  <Td align="right" className="text-xs text-ink-muted">
                    {formatBytes(report.pdfBytes)}
                  </Td>
                  <Td align="right">
                    <Link
                      to={`/reports/${report.id}`}
                      className="text-xs font-medium text-[var(--brand-accent)] hover:underline"
                    >
                      View
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Detail
 * ------------------------------------------------------------------ */

interface ReportDetail {
  /** V3 release gate. Null means the patient cannot see this report. */
  releasedAt: string | null;
  /** True only when the safety engine is holding it, not clinician-reversible. */
  safetyHold: boolean;
  holdReason: string | null;
  id: string;
  generatedAt: string;
  comparisonType: string;
  pdfUrl: string | null;
  pdfBytes: number | null;
  pdfSha256: string | null;
  patient: { id: string; firstName: string; lastName: string; externalId: string | null };
  labReport: { id: string; collectionDate: string };
  scoreRun: {
    overallScore: number | null;
    metabolicScore: number | null;
    inflammationScore: number | null;
    liverScore: number | null;
    confidence: string;
    validationStatus: string;
  };
  inbodyCurrent: { id: string; scanDate: string } | null;
  inbodyBaseline: { id: string; scanDate: string } | null;
}

export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['report', id],
    queryFn: () => api.get<ReportDetail>(`/reports/${id}`),
    enabled: Boolean(id),
  });

  const { data: snapshot } = useQuery({
    queryKey: ['report', id, 'snapshot'],
    queryFn: () => api.get<ReportModel>(`/reports/${id}/snapshot`),
    enabled: Boolean(id),
  });

  const notifyMutation = useMutation({
    mutationFn: () =>
      api.post<{ sms: string | null; email: string | null }>(`/reports/${id}/notify`, {
        attachPdf: false,
      }),
    onSuccess: (result) => {
      const parts = [
        result.sms && `SMS: ${result.sms}`,
        result.email && `Email: ${result.email}`,
      ].filter(Boolean);
      setMessage(
        parts.length > 0
          ? `Notification queued. ${parts.join(' · ')}`
          : 'Nothing to send, the patient has no contact details on file.',
      );
    },
    onError: (caught) =>
      setMessage(caught instanceof Error ? caught.message : 'Could not send the notification'),
  });

  async function download(path: string, filename: string): Promise<void> {
    setBusy(true);
    try {
      const blob = await api.downloadPdf(path);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      // Release the blob once the download has started.
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Download failed');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;
  if (isLoading || !data) return <LoadingRows rows={8} />;

  const filename = `wellness-report-${data.labReport.collectionDate}.pdf`;

  return (
    <>
      <PageHeader
        title="Wellness report"
        subtitle={`${fullName(data.patient)} · collected ${formatDate(data.labReport.collectionDate)}`}
        actions={
          <>
            <Button
              loading={busy}
              onClick={() => void download(`/reports/${data.id}/pdf`, filename)}
            >
              <IconDownload size={15} />
              Download PDF
            </Button>
            {can('ADMIN', 'CLINICIAN') && (
              <>
                <Button
                  variant="secondary"
                  loading={notifyMutation.isPending}
                  onClick={() => notifyMutation.mutate()}
                >
                  Notify patient
                </Button>
                <Button
                  variant="ghost"
                  onClick={() =>
                    void download(`/reports/${data.id}/regenerate`, `rerendered-${filename}`)
                  }
                  title="Re-render from the stored snapshot"
                >
                  Re-render
                </Button>
              </>
            )}
          </>
        }
      />

      {can('ADMIN', 'CLINICIAN') && <PatientReleaseCard report={data} onChanged={refetch} />}

      {message && (
        <div className="mb-5 glass-standard rounded-md2 px-4 py-3 text-sm text-ink">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
        <div className="space-y-6">
          <Card title="Domain scores">
            <div className="grid grid-cols-3 gap-3">
              <ScoreGauge score={data.scoreRun.metabolicScore} label="Metabolic" size={112} />
              <ScoreGauge score={data.scoreRun.inflammationScore} label="Inflammation" size={112} />
              <ScoreGauge score={data.scoreRun.liverScore} label="Liver" size={112} />
            </div>
          </Card>

          {/* Key markers straight from the snapshot, so the app agrees with the PDF. */}
          {snapshot && (
            <Card title="Key marker highlights" padded={false}>
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                {(['metabolic', 'inflammation', 'liver'] as const).map((domain) => (
                  <div key={domain} className="p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-2.5">
                      {domain}
                    </p>
                    <ul className="space-y-1.5">
                      {snapshot.highlights[domain].map((row) => (
                        <li
                          key={row.marker_id}
                          className="flex items-center justify-between gap-2 text-xs"
                        >
                          <span className={row.context_only ? 'text-ink-muted' : 'text-ink'}>
                            {row.label}
                          </span>
                          <span className="flex items-center gap-1.5 shrink-0">
                            <span className="font-medium text-ink">{row.display_value}</span>
                            <TrendArrow direction={row.value_direction} quality={row.trend} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {snapshot?.body_composition?.enabled && (
            <Card title="Body composition">
              <div className="flex justify-center gap-8">
                <div
                  className="w-40"
                  // The figure is deterministic SVG generated by the report
                  // package from this patient's own InBody numbers (§7.3).
                  dangerouslySetInnerHTML={{ __html: snapshot.body_composition.baseline_svg }}
                />
                <div
                  className="w-40"
                  dangerouslySetInnerHTML={{ __html: snapshot.body_composition.current_svg }}
                />
              </div>
              <p className="mt-4 text-sm text-ink leading-relaxed border-l-2 border-[var(--brand-tenant-accent)] pl-3">
                {snapshot.body_composition.summary}
              </p>
            </Card>
          )}

          {snapshot && snapshot.interpretation.length > 0 && (
            <Card title="What this means">
              <ul className="space-y-2">
                {snapshot.interpretation.map((line, index) => (
                  <li key={index} className="text-sm text-ink flex gap-2.5">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--band-optimal)] shrink-0" />
                    {line}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card title="Report details">
            <dl className="text-sm space-y-2.5">
              <Row label="Comparison" value={data.comparisonType.toLowerCase()} />
              <Row label="Confidence" value={data.scoreRun.confidence} />
              <Row label="Validation" value={data.scoreRun.validationStatus} />
              <Row label="Generated" value={formatDateTime(data.generatedAt)} />
              <Row label="Size" value={formatBytes(data.pdfBytes)} />
              {data.inbodyCurrent && (
                <Row label="InBody current" value={formatDate(data.inbodyCurrent.scanDate)} />
              )}
              {data.inbodyBaseline && (
                <Row label="InBody baseline" value={formatDate(data.inbodyBaseline.scanDate)} />
              )}
            </dl>
            {data.pdfSha256 && (
              <div className="mt-4 pt-4 border-t border-ink/5">
                <p className="text-[11px] text-ink-muted mb-1">PDF checksum (SHA-256)</p>
                <p className="text-[10px] font-mono text-ink-muted break-all leading-relaxed">
                  {data.pdfSha256}
                </p>
                <p className="text-[10px] text-ink-muted mt-1.5">
                  Re-rendering the stored snapshot should reproduce this exact checksum.
                </p>
              </div>
            )}
          </Card>

          {snapshot && snapshot.priorities.length > 0 && (
            <Card title="Top priorities">
              <ol className="space-y-3">
                {snapshot.priorities.map((priority) => (
                  <li key={priority.rank} className="flex gap-2.5">
                    <span
                      className="grid place-items-center h-5 w-5 rounded-full text-[10px] font-bold text-white shrink-0 mt-0.5"
                      style={{ background: priority.color }}
                    >
                      {priority.rank}
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-ink">{priority.title}</p>
                      <p className="text-[11px] text-ink-muted mt-0.5 leading-relaxed">
                        {priority.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {snapshot && snapshot.warnings.length > 0 && (
            <Card title="Notes">
              <ul className="space-y-2 text-xs text-ink-muted">
                {snapshot.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </Card>
          )}

          {snapshot?.report.test_label && (
            <div className="rounded-md2 border border-red-200 bg-red-50 px-4 py-3">
              <Pill className="bg-red-600 text-white ring-red-600">
                {snapshot.report.test_label}
              </Pill>
              <p className="mt-2 text-[11px] text-red-800 leading-relaxed">
                Your clinic is in test mode, so every report carries this label. Turn it off in
                Settings before you report on real patients.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink capitalize text-right">{value}</dd>
    </div>
  );
}


/**
 * The patient release gate, the clinician-facing half.
 *
 * The report generates automatically; this single click is what makes it
 * visible in the patient portal. No data entry, but nothing reaches a patient
 * unreviewed, which is what lets the report carry a clinician's name honestly.
 *
 * Three states, and they are deliberately not interchangeable:
 *
 *   - HELD BY SAFETY: a critical value is outstanding. There is no button,
 *     because this is not overridable from a report screen, it is resolved on
 *     the alert.
 *   - NOT RELEASED: awaiting review. One button.
 *   - RELEASED: visible to the patient, with the option to withdraw.
 */
function PatientReleaseCard({
  report,
  onChanged,
}: {
  report: ReportDetail;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const release = useMutation({
    mutationFn: () => api.post(`/reports/${report.id}/release`, {}),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Could not release the report'),
  });

  const withdraw = useMutation({
    mutationFn: () => api.post(`/reports/${report.id}/unrelease`, { reason: reason.trim() }),
    onSuccess: () => {
      setWithdrawing(false);
      setReason('');
      setError(null);
      onChanged();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Could not withdraw the report'),
  });

  if (report.safetyHold) {
    return (
      <Card className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertBadge kind="clinical" />
              <Pill className="bg-amber/20 text-amber-deep">Held</Pill>
            </div>
            <p className="font-display text-sm font-bold text-ink">
              This report is held by the safety engine
            </p>
            <p className="mt-1 text-sm text-ink-muted max-w-xl">
              {report.holdReason ?? 'A critical value is outstanding.'} It cannot be released
              from this screen, resolve the alert first.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            {report.releasedAt ? (
              <Pill className="bg-sage/20 text-sage-deep">Visible to patient</Pill>
            ) : (
              <Pill className="bg-ink/5 text-ink-muted">Pending release</Pill>
            )}
          </div>
          <p className="font-display text-sm font-bold text-ink">
            {report.releasedAt ? 'The patient can see this report' : 'Not yet shared with the patient'}
          </p>
          <p className="mt-1 text-sm text-ink-muted max-w-xl">
            {report.releasedAt
              ? `Released ${new Date(report.releasedAt).toLocaleString()}. Withdrawing removes it from their portal immediately.`
              : 'Releasing makes this report visible in the patient portal. Nothing is sent to them automatically.'}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {report.releasedAt ? (
            <Button variant="secondary" onClick={() => setWithdrawing((open) => !open)}>
              Withdraw
            </Button>
          ) : (
            <Button loading={release.isPending} onClick={() => release.mutate()}>
              Release to patient
            </Button>
          )}
        </div>
      </div>

      {withdrawing && (
        <div className="mt-4 glass-subtle rounded-sm2 p-3">
          <label className="block text-xs font-semibold text-ink mb-1.5">
            Why are you withdrawing it?
          </label>
          <div className="flex flex-wrap gap-2">
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. released in error, awaiting a corrected result"
              className="flex-1 min-w-[18rem] rounded-control border border-ink/10 bg-white/60 px-3 py-2 text-sm text-ink"
            />
            <Button
              variant="secondary"
              disabled={reason.trim().length < 3}
              loading={withdraw.isPending}
              onClick={() => withdraw.mutate()}
            >
              Withdraw report
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-ink-muted">
            The reason is recorded for the clinic and is never shown to the patient.
          </p>
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </Card>
  );
}
